import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useClientMinimalList, ClientMinimal } from '../../../hooks/useClients';
import downArrow from '../../../assets/images/down-arrow.svg';

export type ClientMultiSelectOption = Pick<ClientMinimal, 'id' | 'name'>;

export const ClientMultiSelectWithSearch: React.FC<{
  label?: string;
  selectedClientIds: number[];
  /** Known selected clients (for chip labels when they fall out of the search page). */
  selectedClients?: ClientMultiSelectOption[];
  onChange: (clientIds: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({
  label,
  selectedClientIds,
  selectedClients = [],
  onChange,
  placeholder = 'Select clients',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [knownClients, setKnownClients] = useState<Map<number, string>>(() => {
    const map = new Map<number, string>();
    selectedClients.forEach((c) => {
      if (c.id != null) map.set(c.id, c.name);
    });
    return map;
  });

  const { data: clients, isFetching } = useClientMinimalList({
    search: search.trim() || undefined,
    all_statuses: true,
  });

  useEffect(() => {
    setKnownClients((prev) => {
      const next = new Map(prev);
      selectedClients.forEach((c) => {
        if (c.id != null) next.set(c.id, c.name);
      });
      (clients ?? []).forEach((c) => {
        if (c.id != null) next.set(c.id, c.name);
      });
      return next;
    });
  }, [clients, selectedClients]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const selectedSet = useMemo(() => new Set(selectedClientIds), [selectedClientIds]);

  const buttonLabel = useMemo(() => {
    if (isFetching && !clients?.length && !selectedClientIds.length) return 'Searching...';
    if (!selectedClientIds.length) return placeholder;
    const names = selectedClientIds.map((id) => knownClients.get(id) ?? `#${id}`);
    if (names.length <= 2) return names.join(', ');
    return `${names[0]}, ${names[1]} +${names.length - 2} more`;
  }, [isFetching, clients, selectedClientIds, knownClients, placeholder]);

  const handleToggle = (clientId: number) => {
    if (selectedSet.has(clientId)) {
      onChange(selectedClientIds.filter((id) => id !== clientId));
    } else {
      onChange([...selectedClientIds, clientId]);
    }
  };

  const displayClients = clients ?? [];

  return (
    <div className="flex flex-col gap-1" ref={containerRef}>
      {label && <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</label>}
      <div className="relative">
        <button
          type="button"
          className="focus:ring-blue-primary flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setIsOpen((p) => !p)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          disabled={disabled}
          data-testid="client-multi-select-button"
        >
          <span className="truncate">{buttonLabel}</span>
          <img src={downArrow} alt="" className={`ml-2 h-2 w-2 transform transition ${isOpen ? '' : 'rotate-180'}`} />
        </button>
        {isOpen && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-100 px-2 py-2">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Type to search clients..."
                className="focus:ring-blue-primary w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="max-h-60 overflow-y-auto">
              {isFetching && !displayClients.length ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
              ) : displayClients.length ? (
                displayClients.map((client) => {
                  const id = client.id!;
                  const checked = selectedSet.has(id);
                  return (
                    <label
                      key={id}
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        className="text-blue-primary focus:ring-blue-primary h-4 w-4 rounded border-gray-300"
                        checked={checked}
                        onChange={() => handleToggle(id)}
                      />
                      {client.name} (#{id})
                    </label>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {search.trim() ? 'No clients match your search.' : 'Type to search for clients.'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {selectedClientIds.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {selectedClientIds.map((id) => (
            <span
              key={id}
              className="flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-800"
            >
              {knownClients.get(id) ?? `#${id}`}
              <button
                type="button"
                onClick={() => handleToggle(id)}
                className="ml-0.5 text-blue-500 hover:text-blue-800"
                disabled={disabled}
                aria-label={`Remove client ${id}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
